// PREZENCE — Admin Activity Monitor (FIXED: ESLint + real-time timer)
import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { todayLocal } from '../../utils/localDate';

const fmt = m => { const h=Math.floor(m/60),min=m%60; return h>0?`${h}h ${min}m`:`${min}m`; };
const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const COLORS = ['#63dcbc','#3ba8ff','#a78bfa','#f59e0b','#22c55e','#ef4444'];
const statusColor = t => ({ work:'#22c55e', break:'#3ba8ff', meeting:'#a78bfa', idle:'#f59e0b' })[t]||'#64748b';

export default function AdminActivity() {
  const [employees, setEmployees] = useState([]);
  const [selEmp, setSelEmp]       = useState('');
  const [selDate, setSelDate]     = useState(todayLocal());
  const [activity, setActivity]   = useState(null);
  const [apps, setApps]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [empInfo, setEmpInfo]     = useState(null);
  const [liveSeconds, setLiveSeconds] = useState(0);

  // Load employees once
  useEffect(() => {
    api.get('/users').then(r => {
      const emps = r.data.data || [];
      setEmployees(emps);
      if (emps.length > 0) {
        setSelEmp(emps[0].id);
        setEmpInfo(emps[0]);
      }
    }).catch(() => toast.error('Failed to load employees'));
  }, []);

  const fetchActivity = useCallback(async (empId, date) => {
    if (!empId) return;
    setLoading(true);
    try {
      const [actRes, appRes] = await Promise.all([
        api.get(`/activity/today?user_id=${empId}&date=${date}`),
        api.get(`/apps/summary?user_id=${empId}&date=${date}`),
      ]);
      setActivity(actRes.data);
      setApps(appRes.data.apps || []);
    } catch { toast.error('Failed to load activity'); }
    finally { setLoading(false); }
  }, []);

  // Fetch when employee or date changes
  useEffect(() => {
    if (selEmp && selDate) {
      fetchActivity(selEmp, selDate);
    }
  }, [selEmp, selDate, fetchActivity]);

  // Auto-refresh every 30s for today's data
  useEffect(() => {
    const today = todayLocal();
    if (selDate !== today || !selEmp) return;
    const interval = setInterval(() => fetchActivity(selEmp, selDate), 30000);
    return () => clearInterval(interval);
  }, [selEmp, selDate, fetchActivity]);

  // Live elapsed timer for current active session
  useEffect(() => {
    const currentSession = activity?.sessions?.find(s => !s.ended_at);
    if (!currentSession) { setLiveSeconds(0); return; }
    const start = new Date(currentSession.started_at);
    const tick = () => setLiveSeconds(Math.floor((Date.now() - start) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [activity]);

  const handleEmpChange = (id) => {
    setSelEmp(id);
    setEmpInfo(employees.find(e => e.id === id));
  };

  const fmtSec = s => {
    const h=Math.floor(s/3600), m=Math.floor((s%3600)/60), sec=s%60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  };

  const s = activity?.summary || { work:0, break:0, meeting:0, idle:0 };
  const score = activity?.score || 0;
  const currentSession = activity?.sessions?.find(sess => !sess.ended_at);
  const empIdx = employees.findIndex(e => e.id === selEmp);

  const chartData = [
    { name:'Work',    min:s.work,    fill:'#22c55e' },
    { name:'Break',   min:s.break,   fill:'#3ba8ff' },
    { name:'Meeting', min:s.meeting, fill:'#a78bfa' },
    { name:'Idle',    min:s.idle,    fill:'rgba(245,158,11,0.6)' },
  ];

  const today = todayLocal();

  return (
    <div style={{ animation:'fadeIn 0.4s ease' }}>
      <div className="page-header">
        <h1 className="page-title">Activity Monitor</h1>
        <p className="page-sub">Real-time activity, productivity score and app usage per employee</p>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom:20, padding:'16px 20px' }}>
        <div style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={{ flex:'1 1 200px' }}>
            <label className="form-label">SELECT EMPLOYEE</label>
            <select className="form-select" value={selEmp} onChange={e => handleEmpChange(e.target.value)}>
              <option value="">Choose employee...</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.full_name} — {e.department || 'No dept'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">DATE</label>
            <input className="form-input" type="date" value={selDate}
              onChange={e => setSelDate(e.target.value)}
              max={today} style={{ width:'auto' }} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => fetchActivity(selEmp, selDate)} disabled={loading || !selEmp} style={{ alignSelf:'flex-end' }}>
            {loading ? <><div className="spinner" style={{width:13,height:13}} /> Loading...</> : '🔄 Refresh'}
          </button>
          {selDate === today && <div style={{ alignSelf:'flex-end', fontSize:11, color:'#22c55e', fontFamily:"'JetBrains Mono'", display:'flex', alignItems:'center', gap:5 }}>
            <div style={{ width:6,height:6,borderRadius:'50%',background:'#22c55e',animation:'pulse-dot 1.5s infinite' }} />
            Auto-refresh every 30s
          </div>}
        </div>
      </div>

      {/* Employee info banner */}
      {empInfo && (
        <div className="card" style={{ marginBottom:20, padding:'14px 20px', display:'flex', alignItems:'center', gap:14 }}>
          <div className="avatar avatar-lg" style={{ background:`linear-gradient(135deg,${COLORS[empIdx%COLORS.length]||'#63dcbc'},${COLORS[(empIdx+1)%COLORS.length]||'#3ba8ff'})` }}>
            {getInitials(empInfo.full_name)}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:16, fontWeight:800, color:'#f0f6ff', fontFamily:"'Playfair Display',serif", marginBottom:3 }}>{empInfo.full_name}</div>
            <div style={{ fontSize:12, color:'#475569', marginBottom:4 }}>{empInfo.department} · {empInfo.designation}</div>
            <div style={{ fontSize:11, color:'#334155', fontFamily:"'JetBrains Mono'" }}>
              Shift: {empInfo.shift_start?.slice(0,5)} – {empInfo.shift_end?.slice(0,5)} · {empInfo.is_remote ? '🌐 Remote' : '🏢 In-office'}
            </div>
            {/* Live current status */}
            {currentSession && selDate === today && (
              <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:8,height:8,borderRadius:'50%',background:statusColor(currentSession.session_type),boxShadow:`0 0 8px ${statusColor(currentSession.session_type)}`,animation:'pulse-dot 2s infinite' }} />
                <span style={{ fontSize:12, fontWeight:700, color:statusColor(currentSession.session_type), textTransform:'capitalize' }}>
                  {currentSession.session_type}
                </span>
                <span style={{ fontFamily:"'JetBrains Mono'", fontSize:14, color:'#63dcbc', fontWeight:700 }}>
                  {fmtSec(liveSeconds)}
                </span>
              </div>
            )}
          </div>
          {/* Productivity circle */}
          <div style={{ textAlign:'center', flexShrink:0 }}>
            <div style={{ position:'relative', width:70, height:70 }}>
              <svg viewBox="0 0 70 70" style={{ transform:'rotate(-90deg)' }}>
                <circle cx="35" cy="35" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7" />
                <circle cx="35" cy="35" r="28" fill="none"
                  stroke={score>=80?'#22c55e':score>=60?'#3ba8ff':'#f59e0b'}
                  strokeWidth="7"
                  strokeDasharray={`${(score/100)*176} 176`}
                  strokeLinecap="round"
                  style={{ transition:'stroke-dasharray 1s ease' }}
                />
              </svg>
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'JetBrains Mono'", fontSize:16, fontWeight:700, color:score>=80?'#22c55e':score>=60?'#3ba8ff':'#f59e0b' }}>{score}%</div>
            </div>
            <div style={{ fontSize:10, color:'#475569', marginTop:4 }}>Productivity</div>
          </div>
        </div>
      )}

      {loading && !activity ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}><div className="spinner" style={{width:28,height:28}} /></div>
      ) : !selEmp ? (
        <div className="card"><div className="empty-state"><div className="empty-state-icon">👆</div><div className="empty-state-text">Select an employee above to view their activity</div></div></div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid-4" style={{ marginBottom:20 }}>
            {[
              { label:'Work Time',   value:fmt(s.work),    color:'#22c55e' },
              { label:'Break Time',  value:fmt(s.break),   color:'#3ba8ff' },
              { label:'In Meetings', value:fmt(s.meeting), color:'#a78bfa' },
              { label:'Idle Time',   value:fmt(s.idle),    color:'#f59e0b' },
            ].map((st,i) => (
              <div key={i} className="card" style={{ padding:'16px 18px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:'#475569', fontFamily:"'JetBrains Mono'", letterSpacing:'0.1em', marginBottom:8 }}>{st.label.toUpperCase()}</div>
                <div style={{ fontSize:24, fontWeight:800, color:st.color, fontFamily:"'JetBrains Mono'" }}>{st.value}</div>
              </div>
            ))}
          </div>

          <div className="grid-2" style={{ marginBottom:20 }}>
            {/* Chart */}
            <div className="card">
              <div className="card-header"><span className="card-title">TIME DISTRIBUTION</span></div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} margin={{ left:-20 }}>
                  <XAxis dataKey="name" tick={{ fill:'#64748b', fontSize:12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}m`} />
                  <Tooltip contentStyle={{ background:'#0d1520', border:'1px solid rgba(56,189,248,0.2)', borderRadius:8, fontSize:12, color:'#e2e8f0' }} formatter={v=>[`${v} min`]} />
                  {chartData.map((d,i) => <Bar key={i} dataKey="min" data={[d]} fill={d.fill} radius={[4,4,0,0]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Apps */}
            <div className="card">
              <div className="card-header"><span className="card-title">APP USAGE</span></div>
              {apps.length === 0 ? (
                <div className="empty-state" style={{ padding:30 }}>
                  <div className="empty-state-icon" style={{ fontSize:28 }}>💻</div>
                  <div className="empty-state-text" style={{ fontSize:12 }}>No app data recorded yet</div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {apps.slice(0,6).map((app,i) => (
                    <div key={i}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ width:6,height:6,borderRadius:'50%',background:app.is_productive?'#22c55e':'#f59e0b',flexShrink:0 }} />
                          <span style={{ fontSize:12, color:'#94a3b8', fontWeight:600 }}>{app.name}</span>
                        </div>
                        <div style={{ display:'flex', gap:8 }}>
                          <span style={{ fontSize:11, color:'#64748b', fontFamily:"'JetBrains Mono'" }}>{app.minutes}m</span>
                          <span style={{ fontSize:11, fontFamily:"'JetBrains Mono'", color:app.is_productive?'#22c55e':'#f59e0b' }}>{app.percentage}%</span>
                        </div>
                      </div>
                      <div className="progress-bar-wrap">
                        <div className="progress-bar-fill" style={{ width:`${app.percentage}%`, background:app.is_productive?'#22c55e':'#f59e0b' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">SESSION TIMELINE</span>
              {activity?.sessions?.length > 0 && (
                <span style={{ fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'" }}>{activity.sessions.length} sessions</span>
              )}
            </div>
            {!activity?.sessions?.length ? (
              <div className="empty-state">
                <div className="empty-state-icon">📊</div>
                <div className="empty-state-text">No sessions for {selDate === today ? 'today' : selDate}</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                {activity.sessions.map((sess,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ width:10,height:10,borderRadius:'50%',background:statusColor(sess.session_type),flexShrink:0,boxShadow:`0 0 6px ${statusColor(sess.session_type)}` }} />
                    <div style={{ width:75, fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'", flexShrink:0 }}>
                      {new Date(sess.started_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
                    </div>
                    <span className="badge" style={{ background:`${statusColor(sess.session_type)}20`, color:statusColor(sess.session_type), border:`1px solid ${statusColor(sess.session_type)}40`, flexShrink:0 }}>
                      {sess.session_type.charAt(0).toUpperCase()+sess.session_type.slice(1)}
                    </span>
                    <div style={{ fontSize:12, color:'#64748b' }}>
                      {!sess.ended_at ? (
                        <span style={{ color:'#22c55e', fontFamily:"'JetBrains Mono'", fontWeight:600 }}>
                          {fmtSec(liveSeconds)} ⬤ live
                        </span>
                      ) : `${sess.duration_minutes || 0} min`}
                    </div>
                    {sess.ended_at && (
                      <div style={{ marginLeft:'auto', fontSize:10, color:'#334155', fontFamily:"'JetBrains Mono'" }}>
                        → {new Date(sess.ended_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
